<script lang="ts">
  // Compass for the direction the wind comes FROM. Drag or tap around the ring; arrow keys step
  // by 5° (Shift: 22.5°). The arrow shows where the air goes, as on a weather map.
  import { compass } from '../lib/units';

  let { from, calm = false, onchange }: { from: number; calm?: boolean; onchange: (deg: number) => void } = $props();

  let svg: SVGSVGElement;
  let dragging = false;

  function angleAt(e: PointerEvent): number {
    const r = svg.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    return (Math.round((deg + 360) / 5) * 5) % 360;
  }
  const down = (e: PointerEvent) => {
    dragging = true;
    svg.setPointerCapture(e.pointerId);
    onchange(angleAt(e));
  };
  const move = (e: PointerEvent) => dragging && onchange(angleAt(e));
  const up = () => (dragging = false);
  const key = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 22.5 : 5;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') onchange(Math.round((from + step) % 360));
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') onchange(Math.round((from - step + 360) % 360));
    else return;
    e.preventDefault();
  };
  const ticks = Array.from({ length: 16 }, (_, i) => i * 22.5);
</script>

<svg
  bind:this={svg}
  class="dial"
  class:calm
  viewBox="-60 -60 120 120"
  role="slider"
  tabindex="0"
  aria-label="Wind direction (from)"
  aria-valuemin={0}
  aria-valuemax={355}
  aria-valuenow={from}
  aria-valuetext="from {from}° {compass(from)}"
  onpointerdown={down}
  onpointermove={move}
  onpointerup={up}
  onpointercancel={up}
  onkeydown={key}
>
  <circle r="54" class="ring" />
  {#each ticks as t (t)}
    <line
      x1="0"
      y1={t % 90 === 0 ? -54 : -54}
      x2="0"
      y2={t % 90 === 0 ? -46 : -50}
      transform="rotate({t})"
      class="tick"
    />
  {/each}
  <text y="-30" class="cardinal">N</text>
  <text x="34" y="4" class="cardinal">E</text>
  <text y="38" class="cardinal">S</text>
  <text x="-34" y="4" class="cardinal">W</text>
  <g transform="rotate({from})">
    <!-- handle on the ring at the FROM bearing, arrow pointing downwind through the centre -->
    <line x1="0" y1="-43" x2="0" y2="14" class="shaft" />
    <path d="M0 24 L-7 12 L7 12 Z" class="head" />
    <circle cy="-50" r="6.5" class="handle" />
  </g>
</svg>

<style>
  .dial {
    width: 132px;
    height: 132px;
    touch-action: none;
    cursor: grab;
    flex: none;
  }
  .dial:focus-visible {
    outline: 2px solid var(--accent);
    border-radius: 50%;
  }
  .ring {
    fill: #f8fafc;
    stroke: var(--line);
    stroke-width: 2;
  }
  .tick {
    stroke: #94a3b8;
    stroke-width: 1.5;
  }
  .cardinal {
    font-size: 10px;
    font-weight: 700;
    fill: #475569;
    text-anchor: middle;
    pointer-events: none;
  }
  .shaft {
    stroke: var(--accent);
    stroke-width: 3.5;
    stroke-linecap: round;
  }
  .head {
    fill: var(--accent);
  }
  .handle {
    fill: #fff;
    stroke: var(--accent);
    stroke-width: 3;
  }
  .calm .shaft,
  .calm .head {
    opacity: 0.25;
  }
</style>
